import React, { useState, useEffect, useRef } from 'react';
import { User, FileText, Upload, X, CheckCircle, Plus, ChevronDown, MapPin } from 'lucide-react';
import BillingAddressForm from '../common/BillingAddressForm';
import {
    PROSTHESIS_TYPES,
    PROSTHESIS_TYPE_LABELS,
    ADJOINTE_SUBTYPES,
    ADJOINTE_SUBTYPE_LABELS,
    CONJOINTE_SUBTYPES,
    CONJOINTE_SUBTYPE_LABELS
} from '../../utils/constants';
import ToothSelector from '../common/ToothSelector';
import FileUploadZone from '../common/FileUploadZone';
import { addOrder, updateOrder } from '../../utils/storage';
import { filesApi, authApi } from '../../services/api';
import PatientAutocomplete from '../common/PatientAutocomplete';
import { normalizePatientNameCode } from '../../utils/nameNormalizer';
import { isFieldEditable } from '../../utils/permissions';
import { validateFiles } from '../../utils/fileValidation';
import { validateSiret, validateVatNumber } from '../../utils/businessIdValidation';

const OrderForm = ({
    onSubmitSuccess,
    onCancel,
    mode = 'create',
    initialData = null,
    editableFields = 'all',
    userType = 'client'
}) => {
    const isEditMode = mode === 'edit';
    const [userProfile, setUserProfile] = useState(null);
    const [hasDefaultBilling, setHasDefaultBilling] = useState(false);
    // En création, les sections "Coordonnées" et "Adresse de facturation" sont collapsed
    // par défaut quand elles sont déjà pré-remplies (profil utilisateur existant).
    // En édition, toujours expanded.
    const [coordinatesCollapsed, setCoordinatesCollapsed] = useState(false);
    const [billingCollapsed, setBillingCollapsed] = useState(false);

    const createEmptyWork = () => ({
        prosthesisType: '',
        prosthesisSubtype: '',
        toothNumbers: [],
        shade: '',
        description: '',
        hasDigitalModel: false
    });

    // Helper to check if a field is editable
    const canEditField = (fieldName) => {
        if (!isEditMode) return true;
        if (editableFields === 'all') return true;
        return isFieldEditable(fieldName, userType);
    };

    // Initialize form data based on mode
    const getInitialFormData = () => {
        if (isEditMode && initialData) {
            // Convert delivery date to YYYY-MM-DD format for input
            const deliveryDate = initialData.deliveryDate
                ? new Date(initialData.deliveryDate).toISOString().split('T')[0]
                : '';

            return {
                name: initialData.clientInfo?.name || '',
                clinic: initialData.clientInfo?.clinic || '',
                phone: initialData.clientInfo?.phone || '',
                email: initialData.clientInfo?.email || '',
                address: initialData.clientInfo?.address || '',
                billingAddress: {
                    billingName: initialData.billing_name || '',
                    billingAddressLine1: initialData.billing_address_line1 || '',
                    billingAddressLine2: initialData.billing_address_line2 || '',
                    billingPostalCode: initialData.billing_postal_code || '',
                    billingCity: initialData.billing_city || '',
                    billingCountry: initialData.billing_country || 'France',
                    siret: initialData.billing_siret || '',
                    vatNumber: initialData.billing_vat_number || ''
                },
                saveBillingAddress: false,
                patientNameCode: initialData.patient_name_code || initialData.patientNameCode || '',
                works: initialData.works && initialData.works.length > 0
                    ? initialData.works.map(w => ({
                        prosthesisType: w.prosthesisType || '',
                        prosthesisSubtype: w.prosthesisSubtype || '',
                        toothNumbers: w.toothNumbers || [],
                        shade: w.shade || '',
                        description: w.description || '',
                        hasDigitalModel: !!w.hasDigitalModel
                    }))
                    : [createEmptyWork()],
                deliveryDate: deliveryDate,
                files: []
            };
        }
        return {
            name: '',
            clinic: '',
            phone: '',
            email: '',
            address: '',
            billingAddress: {
                billingName: '',
                billingAddressLine1: '',
                billingAddressLine2: '',
                billingPostalCode: '',
                billingCity: '',
                billingCountry: 'France',
                siret: '',
                vatNumber: ''
            },
            saveBillingAddress: false,
            patientNameCode: '',
            works: [createEmptyWork()],
            deliveryDate: '',
            files: []
        };
    };

    const [formData, setFormData] = useState(getInitialFormData);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const errorRef = useRef(null);

    // Fait remonter le message d'erreur dans le viewport : le bouton de
    // soumission est en bas du formulaire, l'erreur s'affiche tout en haut.
    useEffect(() => {
        if (errorMessage) {
            errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [errorMessage]);

    // Charger le profil utilisateur (seulement en mode création)
    useEffect(() => {
        if (isEditMode) return; // Skip profile loading in edit mode

        const loadUserProfile = async () => {
            try {
                const profile = await authApi.getProfile();
                setUserProfile(profile);

                // Load billing address
                const billingAddress = await authApi.getBillingAddress();
                const hasBilling = billingAddress !== null;
                setHasDefaultBilling(hasBilling);

                setFormData(prev => ({
                    ...prev,
                    name: profile.name || '',
                    clinic: profile.clinic_name || '',
                    phone: profile.phone || '',
                    email: profile.email || '',
                    address: profile.address || '',
                    billingAddress: billingAddress || {
                        billingName: '',
                        billingAddressLine1: '',
                        billingAddressLine2: '',
                        billingPostalCode: '',
                        billingCity: '',
                        billingCountry: 'France',
                        siret: '',
                        vatNumber: ''
                    },
                    // Pre-check save checkbox if user doesn't have billing address yet
                    saveBillingAddress: !hasBilling
                }));

                // Collapser les sections déjà pré-remplies pour aller directement
                // à la fiche de commande.
                if (profile.name && profile.phone && profile.email) {
                    setCoordinatesCollapsed(true);
                }
                if (hasBilling) {
                    setBillingCollapsed(true);
                }
            } catch (error) {
                console.error('Erreur lors du chargement du profil utilisateur:', error);
            }
        };

        loadUserProfile();
    }, [isEditMode]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Reset subtype when prosthesis type changes
        if (name === 'prosthesisType') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                prosthesisSubtype: ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleWorkChange = (index, field, value) => {
        setFormData(prev => {
            const newWorks = [...prev.works];
            if (field === 'prosthesisType') {
                newWorks[index] = { ...newWorks[index], [field]: value, prosthesisSubtype: '' };
            } else {
                newWorks[index] = { ...newWorks[index], [field]: value };
            }
            return { ...prev, works: newWorks };
        });
    };

    const addWork = () => {
        setFormData(prev => ({
            ...prev,
            works: [...prev.works, createEmptyWork()]
        }));
    };

    const removeWork = (index) => {
        if (formData.works.length <= 1) return;
        setFormData(prev => ({
            ...prev,
            works: prev.works.filter((_, i) => i !== index)
        }));
    };

    const getSubtypeOptionsForWork = (prosthesisType) => {
        if (prosthesisType === PROSTHESIS_TYPES.ADJOINTE) {
            return Object.entries(ADJOINTE_SUBTYPE_LABELS);
        }
        if (prosthesisType === PROSTHESIS_TYPES.CONJOINTE) {
            return Object.entries(CONJOINTE_SUBTYPE_LABELS);
        }
        return [];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrorMessage('');

        try {
            // Validate billing address
            const billing = formData.billingAddress;
            const failBilling = (msg) => {
                setErrorMessage(msg);
                setBillingCollapsed(false);
                setIsSubmitting(false);
            };
            if (!billing.billingName?.trim()) {
                failBilling('Le nom ou raison sociale de facturation est requis');
                return;
            }
            if (!billing.billingAddressLine1?.trim()) {
                failBilling('L\'adresse de facturation est requise');
                return;
            }
            if (!billing.billingPostalCode?.trim()) {
                failBilling('Le code postal de facturation est requis');
                return;
            }
            if (!billing.billingCity?.trim()) {
                failBilling('La ville de facturation est requise');
                return;
            }
            if (!billing.siret?.trim()) {
                failBilling('Le numéro SIRET ou SIREN de facturation est requis');
                return;
            }
            const siretCheck = validateSiret(billing.siret);
            if (!siretCheck.valid) {
                failBilling(siretCheck.error);
                return;
            }
            const vatCheck = validateVatNumber(billing.vatNumber);
            if (!vatCheck.valid) {
                failBilling(vatCheck.error);
                return;
            }

            // Description obligatoire à la création (le natif `required` laisse
            // passer les espaces seuls, d'où ce contrôle trim en complément).
            if (!isEditMode && formData.works.some(w => !w.description?.trim())) {
                setErrorMessage('La description est obligatoire pour chaque travail');
                setIsSubmitting(false);
                return;
            }

            const orderData = {
                clientInfo: {
                    name: formData.name,
                    clinic: formData.clinic,
                    phone: formData.phone,
                    email: formData.email,
                    address: formData.address
                },
                billingAddress: formData.billingAddress,
                saveBillingAddress: formData.saveBillingAddress,
                patientNameCode: normalizePatientNameCode(formData.patientNameCode),
                works: formData.works.map(w => ({
                    prosthesisType: w.prosthesisType,
                    prosthesisSubtype: w.prosthesisSubtype || null,
                    toothNumbers: w.toothNumbers,
                    shade: w.shade || null,
                    description: w.description || null,
                    hasDigitalModel: !!w.hasDigitalModel
                })),
                deliveryDate: formData.deliveryDate
            };

            if (isEditMode) {
                // Update existing order - returns updated order with system message
                const updatedOrder = await updateOrder(initialData.id, orderData);
                setSubmitSuccess(true);

                setTimeout(() => {
                    if (onSubmitSuccess) {
                        onSubmitSuccess(updatedOrder);
                    }
                }, 1000);
            } else {
                // Create new order
                if (formData.files && formData.files.length > 0) {
                    const validation = validateFiles(formData.files);
                    if (!validation.valid) {
                        setErrorMessage(validation.error);
                        setIsSubmitting(false);
                        return;
                    }
                    const createdOrder = await addOrder(orderData);
                    await filesApi.upload(createdOrder.id, formData.files);
                } else {
                    await addOrder(orderData);
                }

                setSubmitSuccess(true);

                setTimeout(() => {
                    if (onSubmitSuccess) {
                        onSubmitSuccess();
                    }
                }, 1000);
            }
        } catch (error) {
            console.error(`Erreur lors de ${isEditMode ? 'la modification' : 'la création'} de la commande:`, error);
            // Surface the real reason (session expirée, fichier trop volumineux, validation serveur…)
            // instead of a generic message, so the user knows what actually went wrong.
            const fallback = `Erreur lors de ${isEditMode ? 'la modification' : 'la création'} de la commande`;
            setErrorMessage(error?.message || fallback);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-6 relative">
            {/* Overlay de chargement plein écran */}
            {isSubmitting && (
                <div className="fixed inset-0 overlay-frost z-50 flex items-center justify-center">
                    <div className="glass-deep border border-white/10 rounded-2xl p-8 flex flex-col items-center space-y-4 shadow-2xl">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-yellow-500 border-t-transparent"></div>
                        <p className="text-white font-medium text-lg">
                            {isEditMode ? 'Modification en cours...' : 'Création de la commande...'}
                        </p>
                        <p className="text-gray-400 text-sm">Veuillez patienter</p>
                    </div>
                </div>
            )}

            {submitSuccess && (
                <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                    <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
                        <p className="text-sm font-medium text-green-400">
                            {isEditMode ? 'Commande modifiée avec succès !' : 'Commande soumise avec succès !'}
                        </p>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div ref={errorRef} className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                    <p className="text-sm font-medium text-red-400">{errorMessage}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Coordonnees */}
                <div className="bg-black/30 border border-white/10 rounded-xl p-6">
                    <button
                        type="button"
                        onClick={() => setCoordinatesCollapsed(c => !c)}
                        className="w-full flex items-center justify-between mb-4 group"
                        aria-expanded={!coordinatesCollapsed}
                    >
                        <div className="flex items-center">
                            <User className="w-5 h-5 text-yellow-400 mr-2" />
                            <h3 className="text-lg font-semibold text-white">Coordonnées</h3>
                            {coordinatesCollapsed && (formData.name || formData.email) && (
                                <span className="ml-3 text-sm text-gray-400 truncate">
                                    — {[formData.name, formData.clinic].filter(Boolean).join(' · ')}
                                </span>
                            )}
                        </div>
                        <ChevronDown className={`w-5 h-5 text-gray-400 group-hover:text-gray-200 transition-transform ${coordinatesCollapsed ? '' : 'rotate-180'}`} />
                    </button>
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${coordinatesCollapsed ? 'hidden' : ''}`}>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Nom complet *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                disabled={!canEditField('name')}
                                className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                                    canEditField('name')
                                        ? 'bg-black/30 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-yellow-500 focus:border-transparent'
                                        : 'glass border-white/10 text-gray-400 cursor-not-allowed'
                                }`}
                                placeholder="Votre nom"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Cabinet / Organisation
                            </label>
                            <input
                                type="text"
                                name="clinic"
                                value={formData.clinic}
                                onChange={handleInputChange}
                                disabled={!canEditField('clinic')}
                                className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                                    canEditField('clinic')
                                        ? 'bg-black/30 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-yellow-500 focus:border-transparent'
                                        : 'glass border-white/10 text-gray-400 cursor-not-allowed'
                                }`}
                                placeholder="Nom du cabinet"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Téléphone *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                disabled={!canEditField('phone')}
                                className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                                    canEditField('phone')
                                        ? 'bg-black/30 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-yellow-500 focus:border-transparent'
                                        : 'glass border-white/10 text-gray-400 cursor-not-allowed'
                                }`}
                                placeholder="Votre numéro de téléphone"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Email *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                disabled={!canEditField('email')}
                                className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                                    canEditField('email')
                                        ? 'bg-black/30 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-yellow-500 focus:border-transparent'
                                        : 'glass border-white/10 text-gray-400 cursor-not-allowed'
                                }`}
                                placeholder="votre@email.com"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Adresse
                            </label>
                            <input
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                disabled={!canEditField('address')}
                                className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                                    canEditField('address')
                                        ? 'bg-black/30 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-yellow-500 focus:border-transparent'
                                        : 'glass border-white/10 text-gray-400 cursor-not-allowed'
                                }`}
                                placeholder="Adresse du cabinet"
                            />
                        </div>
                    </div>
                </div>

                {/* Adresse de facturation */}
                {billingCollapsed ? (
                    <button
                        type="button"
                        onClick={() => setBillingCollapsed(false)}
                        className="w-full bg-black/30 border border-white/10 rounded-xl p-6 flex items-center justify-between group hover:border-white/15 transition-colors"
                        aria-expanded={false}
                    >
                        <div className="flex items-center text-left min-w-0">
                            <MapPin className="w-5 h-5 text-blue-400 mr-2 flex-shrink-0" />
                            <h3 className="text-lg font-semibold text-white">Adresse de facturation</h3>
                            <span className="ml-3 text-sm text-gray-400 truncate">
                                — {[formData.billingAddress?.billingName, formData.billingAddress?.billingCity].filter(Boolean).join(' · ')}
                            </span>
                        </div>
                        <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-gray-200 flex-shrink-0" />
                    </button>
                ) : (
                    <BillingAddressForm
                        billingData={formData.billingAddress}
                        onChange={(billingAddress) => setFormData(prev => ({ ...prev, billingAddress }))}
                        disabled={isSubmitting}
                        showSaveCheckbox={!isEditMode}
                        saveToProfile={formData.saveBillingAddress}
                        onSaveToProfileChange={(checked) => setFormData(prev => ({ ...prev, saveBillingAddress: checked }))}
                    />
                )}

                {/* Informations du patient */}
                <div className="bg-black/30 border border-white/10 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                        <User className="w-5 h-5 text-green-400 mr-2" />
                        <h3 className="text-lg font-semibold text-white">Informations du patient</h3>
                    </div>
                    {canEditField('patientNameCode') ? (
                        <PatientAutocomplete
                            patientNameCode={formData.patientNameCode}
                            onPatientNameCodeChange={(patientNameCode) => setFormData(prev => ({ ...prev, patientNameCode }))}
                            onPatientSelect={(patient) => {
                                console.log('Patient sélectionné:', patient);
                            }}
                            clientId={userProfile?.id}
                            className="mb-4"
                            placeholder="Nom/code du patient (ex: JEAN-CLAUDE MARTIN)"
                        />
                    ) : (
                        <input
                            type="text"
                            value={formData.patientNameCode}
                            disabled
                            className="w-full px-4 py-3 glass border border-white/10 text-gray-400 rounded-lg cursor-not-allowed"
                        />
                    )}
                </div>

                {/* Travaux */}
                {formData.works.map((work, workIndex) => (
                    <div key={workIndex} className="bg-black/30 border border-white/10 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <FileText className="w-5 h-5 text-indigo-400 mr-2" />
                                <h3 className="text-lg font-semibold text-white">
                                    Travail {workIndex + 1}
                                </h3>
                            </div>
                            {formData.works.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeWork(workIndex)}
                                    className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-500/20 transition-colors"
                                    title="Supprimer ce travail"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        <div className="flex flex-col lg:flex-row gap-6">
                            {/* Colonne gauche */}
                            <div className="flex-1 flex flex-col min-w-0">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Type de prothèse *
                                    </label>
                                    <select
                                        value={work.prosthesisType}
                                        onChange={(e) => handleWorkChange(workIndex, 'prosthesisType', e.target.value)}
                                        required
                                        className="w-full px-4 py-3 bg-black/30 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                                    >
                                        <option value="">Sélectionnez un type</option>
                                        {Object.entries(PROSTHESIS_TYPE_LABELS).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mt-6 flex-1 flex flex-col">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Dent(s) concernée(s) *
                                    </label>
                                    <ToothSelector
                                        value={work.toothNumbers}
                                        onChange={(e) => handleWorkChange(workIndex, 'toothNumbers', e.target.value)}
                                        name={`work-${workIndex}-toothNumbers`}
                                        required
                                        className="flex-1"
                                    />
                                </div>
                            </div>

                            {/* Colonne droite */}
                            <div className="flex-1 min-w-0 space-y-6">
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${work.prosthesisType && work.prosthesisType !== PROSTHESIS_TYPES.AUTRE ? 'text-gray-300' : 'text-gray-500'}`}>
                                        Sous-type {work.prosthesisType && work.prosthesisType !== PROSTHESIS_TYPES.AUTRE && '*'}
                                    </label>
                                    <select
                                        value={work.prosthesisSubtype}
                                        onChange={(e) => handleWorkChange(workIndex, 'prosthesisSubtype', e.target.value)}
                                        required={work.prosthesisType && work.prosthesisType !== PROSTHESIS_TYPES.AUTRE}
                                        disabled={!work.prosthesisType || work.prosthesisType === PROSTHESIS_TYPES.AUTRE}
                                        className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                                            work.prosthesisType && work.prosthesisType !== PROSTHESIS_TYPES.AUTRE
                                                ? 'bg-black/30 border-white/10 text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent'
                                                : 'glass border-white/10 text-gray-500 cursor-not-allowed'
                                        }`}
                                    >
                                        <option value="">
                                            {!work.prosthesisType
                                                ? 'Sélectionnez d\'abord un type'
                                                : work.prosthesisType === PROSTHESIS_TYPES.AUTRE
                                                    ? 'Non applicable pour ce type'
                                                    : 'Sélectionnez un sous-type'
                                            }
                                        </option>
                                        {getSubtypeOptionsForWork(work.prosthesisType).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Teinte
                                    </label>
                                    <input
                                        type="text"
                                        value={work.shade}
                                        onChange={(e) => handleWorkChange(workIndex, 'shade', e.target.value)}
                                        className="w-full px-4 py-3 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                                        placeholder="Ex: A2, B1, C3..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Modèle 3D souhaité
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-sm font-medium ${!work.hasDigitalModel ? 'text-white' : 'text-gray-500'}`}>
                                            Non
                                        </span>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={work.hasDigitalModel}
                                            onClick={() => handleWorkChange(workIndex, 'hasDigitalModel', !work.hasDigitalModel)}
                                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${work.hasDigitalModel ? 'bg-yellow-500' : 'bg-white/20'}`}
                                        >
                                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${work.hasDigitalModel ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                        <span className={`text-sm font-medium ${work.hasDigitalModel ? 'text-white' : 'text-gray-500'}`}>
                                            Oui
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Description - pleine largeur */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Description détaillée {!isEditMode && '*'}
                            </label>
                            <textarea
                                value={work.description}
                                onChange={(e) => handleWorkChange(workIndex, 'description', e.target.value)}
                                required={!isEditMode}
                                rows={3}
                                className="w-full px-4 py-3 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                                placeholder="Décrivez en détail le travail à effectuer..."
                            />
                        </div>
                    </div>
                ))}

                {/* Bouton ajouter un travail */}
                <button
                    type="button"
                    onClick={addWork}
                    className="w-full py-4 border-2 border-dashed border-white/15 rounded-xl text-gray-400 hover:text-yellow-400 hover:border-yellow-500 transition-colors flex items-center justify-center space-x-2"
                >
                    <Plus className="w-5 h-5" />
                    <span>Ajouter un travail</span>
                </button>

                {/* Date de livraison */}
                <div className="bg-black/30 border border-white/10 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                        <FileText className="w-5 h-5 text-purple-400 mr-2" />
                        <h3 className="text-lg font-semibold text-white">Livraison</h3>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Date de livraison souhaitée *
                        </label>
                        <input
                            type="date"
                            name="deliveryDate"
                            value={formData.deliveryDate}
                            onChange={handleInputChange}
                            required
                            style={{ colorScheme: 'dark' }}
                            className="w-full px-4 py-3 bg-black/30 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                        />
                    </div>
                </div>

                {/* Fichiers - Caché en mode édition */}
                {!isEditMode && (
                    <div className="bg-black/30 border border-white/10 rounded-xl p-6">
                        <div className="flex items-center mb-4">
                            <Upload className="w-5 h-5 text-green-400 mr-2" />
                            <h3 className="text-lg font-semibold text-white">Fichiers joints</h3>
                        </div>
                        <FileUploadZone
                            files={formData.files}
                            onFilesChange={(files) => setFormData(prev => ({ ...prev, files }))}
                            disabled={isSubmitting}
                        />
                    </div>
                )}

                {/* Bouton de soumission */}
                <div className="flex justify-end space-x-4">
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-6 py-3 border border-white/15 text-gray-300 rounded-xl font-medium hover:bg-white/10 transition-colors"
                        >
                            Annuler
                        </button>
                    )}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black px-8 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    >
                        {isSubmitting ? (
                            <div className="flex items-center space-x-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>{isEditMode ? 'Modification en cours...' : 'Envoi en cours...'}</span>
                            </div>
                        ) : (
                            isEditMode ? 'Modifier la commande' : 'Créer la commande'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default OrderForm;
